/**
 * Sample data, shaped exactly like the wire types, plus the query functions the
 * views need. `?demo=1` routes every call in api.ts here.
 *
 * This exists because the real service needs Cloudflare resources — D1, R2, a
 * Durable Object namespace, Queues — that a laptop does not have, so without it
 * there is no way to see whether a view renders at all. The bucketing,
 * filtering and cursor logic below is a deliberate re-implementation of
 * cloud/src/views.ts against arrays; where it diverges from the SQL, the
 * comment says so and why.
 */

import type {
  AlbumSummary, Annotation, AssetCard, Certainty, DateBucket, Filter, ManagedShare, Me,
  PersonPage, PersonSummary, Precision, Slice, Stats, TimelineEntry,
  Tree, TreeEdge, TreeNode,
} from "./types";

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

interface Identity {
  id: string;
  name: string | null;
  birthYear: number | null;
  deathYear: number | null;
}

export const IDENTITIES: Identity[] = [
  { id: "p-patrick", name: "Patrick Doyle", birthYear: 1893, deathYear: 1961 },
  { id: "p-annie", name: "Annie Doyle", birthYear: 1896, deathYear: 1978 },
  { id: "p-arthur", name: "Arthur Coffey", birthYear: 1918, deathYear: 1994 },
  { id: "p-margaret", name: "Margaret Coffey", birthYear: 1921, deathYear: 2009 },
  { id: "p-james", name: "James Coffey", birthYear: 1946, deathYear: null },
  { id: "p-susan", name: "Susan Coffey", birthYear: 1951, deathYear: null },
  { id: "p-eileen", name: "Eileen Walsh", birthYear: 1949, deathYear: 2018 },
  { id: "p-brendan", name: "Brendan Walsh", birthYear: 1947, deathYear: null },
  { id: "p-clare", name: "Clare Walsh", birthYear: 1974, deathYear: null },
  { id: "p-tyler", name: "Tyler Coffey", birthYear: 1979, deathYear: null },
  // Somebody in a great many photographs whom nobody has named yet. The whole
  // point of the "unnamed only" filter is that this row exists.
  { id: "p-unknown-1", name: null, birthYear: null, deathYear: null },
];

const NAME = new Map(IDENTITIES.map((i) => [i.id, i.name] as const));

export const RELATIONSHIPS: TreeEdge[] = [
  { kind: "spouse", from: "p-patrick", to: "p-annie", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-annie", to: "p-margaret", certainty: "stated", endedYear: null },
  // The parish register and the family bible disagree about her father.
  { kind: "parent", from: "p-patrick", to: "p-margaret", certainty: "disputed", endedYear: null },
  { kind: "spouse", from: "p-arthur", to: "p-margaret", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-arthur", to: "p-james", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-margaret", to: "p-james", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-arthur", to: "p-eileen", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-margaret", to: "p-eileen", certainty: "stated", endedYear: null },
  { kind: "spouse", from: "p-james", to: "p-susan", certainty: "stated", endedYear: null },
  { kind: "spouse", from: "p-eileen", to: "p-brendan", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-james", to: "p-tyler", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-susan", to: "p-tyler", certainty: "probable", endedYear: null },
  { kind: "parent", from: "p-eileen", to: "p-clare", certainty: "stated", endedYear: null },
  { kind: "parent", from: "p-brendan", to: "p-clare", certainty: "stated", endedYear: null },
];

// ---------------------------------------------------------------------------
// Photographs and film
// ---------------------------------------------------------------------------

type Row = [
  id: string,
  file: string,
  kind: "image" | "video",
  at: string | null,
  prec: Precision | null,
  source: string | null,
  people: string[],
  caption: string | null,
  durationMs?: number,
  stories?: number,
];

const ROWS: Row[] = [
  ["a-01", "doyle-wedding.tif", "image", "1919-06-14", "day", "written-on-back", ["p-patrick", "p-annie"], "Patrick and Annie outside the church", undefined, 2],
  ["a-02", "scan-0043.tif", "image", "1920-01-01", "decade", "inferred", ["p-annie"], "Annie on the step at Blackrock"],
  ["a-03", "scan-0044.tif", "image", "1920-01-01", "decade", "inferred", ["p-unknown-1"], null],
  ["a-04", "margaret-school.jpg", "image", "1930-01-01", "circa", "inferred", ["p-margaret"], "Margaret, school photograph"],
  ["a-05", "arthur-uniform.tif", "image", "1941-11-01", "month", "written-on-back", ["p-arthur"], "Arthur before he shipped out", undefined, 1],
  ["a-06", "wedding-1945.tif", "image", "1945-09-08", "day", "written-on-back", ["p-arthur", "p-margaret", "p-annie"], "The wedding at Rathmines", undefined, 3],
  ["a-07", "scan-0102.tif", "image", "1947-01-01", "year", "inferred", ["p-margaret", "p-james"], "Margaret with James, a few months old"],
  ["a-08", "scan-0103.tif", "image", "1950-01-01", "decade", "inferred", ["p-james", "p-eileen"], "James and Eileen in the garden"],
  ["a-09", "scan-0104.tif", "image", "1950-01-01", "decade", "inferred", ["p-unknown-1", "p-james"], null],
  ["a-10", "seaside-1953.tif", "image", "1953-08-15", "day", "exif", ["p-margaret", "p-james", "p-eileen"], "Portmarnock", undefined, 1],
  ["a-11", "scan-0155.tif", "image", "1956-07-01", "month", "written-on-back", ["p-arthur", "p-james"], "Arthur teaching James to fish"],
  ["a-12", "scan-0156.tif", "image", "1958-01-01", "year", "inferred", ["p-eileen"], null],
  ["a-13", "communion.tif", "image", "1959-05-24", "day", "written-on-back", ["p-eileen", "p-margaret"], "Eileen's first communion"],
  ["a-14", "reel-01.mp4", "video", "1962-01-01", "circa", "inferred", ["p-margaret", "p-arthur", "p-james", "p-eileen"], "8mm reel, transferred 2019", 214000, 2],
  ["a-15", "scan-0201.tif", "image", "1963-01-01", "decade", "inferred", ["p-unknown-1"], null],
  ["a-16", "scan-0202.tif", "image", "1966-04-10", "day", "exif", ["p-james", "p-susan"], "James and Susan, before they were married"],
  ["a-17", "wedding-1970.jpg", "image", "1970-06-20", "day", "exif", ["p-james", "p-susan", "p-margaret", "p-arthur"], "James and Susan's wedding", undefined, 2],
  ["a-18", "eileen-brendan.jpg", "image", "1971-09-04", "day", "exif", ["p-eileen", "p-brendan"], "Eileen and Brendan"],
  ["a-19", "scan-0288.tif", "image", "1974-01-01", "year", "written-on-back", ["p-clare", "p-eileen"], "Clare, newborn"],
  ["a-20", "reel-02.mp4", "video", "1976-07-01", "month", "written-on-back", ["p-clare", "p-eileen", "p-brendan"], "Clare's second birthday", 96000, 1],
  ["a-21", "scan-0301.tif", "image", "1979-11-02", "day", "written-on-back", ["p-tyler", "p-susan"], "Tyler, a week old"],
  ["a-22", "christmas-1981.jpg", "image", "1981-12-25", "day", "exif", ["p-margaret", "p-arthur", "p-tyler", "p-clare", "p-james", "p-susan"], "Christmas at Nana's", undefined, 4],
  ["a-23", "scan-0344.jpg", "image", "1983-01-01", "year", "inferred", ["p-tyler", "p-clare"], null],
  ["a-24", "scan-0345.jpg", "image", "1985-06-01", "month", "inferred", ["p-arthur"], "Arthur in the greenhouse"],
  ["a-25", "school-1990.jpg", "image", "1990-09-03", "day", "exif", ["p-tyler"], null],
  ["a-26", "nana-1994.jpg", "image", "1994-03-12", "day", "exif", ["p-margaret"], "Nana, the spring Grandad died", undefined, 2],
  ["a-27", "scan-0410.jpg", "image", "1997-01-01", "year", "inferred", ["p-margaret", "p-clare"], null],
  ["a-28", "nana-tells.mp4", "video", "2004-05-16", "day", "exif", ["p-margaret", "p-tyler"], "Nana telling us about the wedding", 1_842_000, 5],
  ["a-29", "IMG_2201.jpg", "image", "2008-08-11", "day", "exif", ["p-margaret", "p-james", "p-eileen"], "Nana's eighty-seventh"],
  ["a-30", "IMG_3390.jpg", "image", "2011-06-04", "day", "exif", ["p-clare", "p-tyler"], null],
  ["a-31", "IMG_4455.jpg", "image", "2014-07-19", "day", "exif", ["p-eileen", "p-brendan", "p-clare"], "The house at Roundstone"],
  ["a-32", "IMG_5501.heic", "image", "2018-02-02", "day", "exif", ["p-eileen"], "The last one of Eileen", undefined, 1],
  ["a-33", "IMG_6612.heic", "image", "2021-10-09", "day", "exif", ["p-tyler", "p-clare", "p-james"], null],
  ["a-34", "unsorted-0001.jpg", "image", null, null, null, ["p-unknown-1"], null],
  ["a-35", "unsorted-0002.jpg", "image", null, null, null, [], null],
  ["a-36", "attic-box-3.tif", "image", "1940-01-01", "decade", "inferred", [], "Nobody knows who these are"],
];

function hashFor(id: string): string {
  // Stable, and shaped like a real content hash so the blob URL builder and the
  // crop-key test in api.ts see what they would see in production.
  let x = 2166136261;
  for (const ch of id) x = ((x ^ ch.charCodeAt(0)) * 16777619) >>> 0;
  return Array.from({ length: 8 }, (_, i) =>
    ((x + i * 2654435761) >>> 0).toString(16).padStart(8, "0"),
  ).join("").slice(0, 64);
}

const ASSETS: AssetCard[] = ROWS.map(
  ([id, file, kind, at, prec, source, people, caption, durationMs, stories]) => ({
    id,
    kind,
    contentHash: hashFor(id),
    proxyKey: null,
    editedKey: null,
    colorizedKey: null,
    fileName: file,
    width: kind === "video" ? 1440 : 2400,
    height: kind === "video" ? 1080 : 1800,
    orientation: 1,
    capturedAt: at,
    capturedPrecision: prec,
    dateSource: source,
    durationMs: durationMs ?? null,
    caption,
    description: caption ? `${caption}. Scanned from the box in the attic.` : null,
    people: people.map((p) => ({ identityId: p, name: NAME.get(p) ?? null })),
    storyCount: stories ?? 0,
    hidden: false,
  }),
);

/**
 * `?demo=1&big=40` — the same box of photographs, forty times over.
 *
 * Thirty-six rows fit on one page and in one screen, which means they exercise
 * neither the cursor paging nor the virtual window. A real inherited archive is
 * five figures. This makes one, cheaply, so both can be watched working.
 */
const BULK = Math.min(400, Math.max(0, Number(new URLSearchParams(location.search).get("big") ?? 0)));
if (BULK > 1) {
  const seed = ASSETS.slice();
  for (let copy = 1; copy < BULK; copy++) {
    for (const a of seed) {
      const id = `${a.id}-c${copy}`;
      ASSETS.push({
        ...a,
        id,
        contentHash: hashFor(id),
        fileName: `box${copy}-${a.fileName}`,
        people: a.people.map((p) => ({ ...p })),
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Stories
// ---------------------------------------------------------------------------

function annot(
  id: string,
  kind: string,
  subject: Annotation["subject_kind"],
  subjectId: string | null,
  body: string,
  extra: Partial<Annotation> = {},
): Annotation {
  return {
    id,
    kind,
    subject_kind: subject,
    subject_id: subjectId,
    body,
    body_key: null,
    body_mime: null,
    duration_ms: null,
    transcript: null,
    transcript_state: null,
    period_start: null,
    period_end: null,
    period_precision: null,
    author_account: "acct-tyler",
    attributed_to: null,
    created_at: "2024-02-11 19:04:00",
    updated_at: "2024-02-11 19:04:00",
    deleted_at: null,
    ...extra,
  };
}

const ANNOTATIONS: Annotation[] = [
  annot("an-01", "narration", "period", null,
    "The summer they all went to the lake and nobody brought a camera. Eileen remembered the boat leaking and Brendan bailing it out with a saucepan.",
    { period_start: "1972-06-01", period_end: "1972-09-01", period_precision: "month", attributed_to: "p-eileen" }),
  annot("an-02", "journal", "period", null,
    "Arthur was away for most of 1942 and 1943. The letters are in the second box; they are the only record of those two years.",
    { period_start: "1942-01-01", period_end: "1943-12-31", period_precision: "year", attributed_to: "p-margaret" }),
  annot("an-03", "narration", "period", null,
    "Nana always said the fifties were the years the house was full. Four of them in two rooms in Rathmines.",
    { period_start: "1950-01-01", period_end: "1959-12-31", period_precision: "decade", attributed_to: "p-margaret" }),
  annot("an-04", "note", "period", null,
    "Nothing survives from this stretch — the flood in the garage took the whole decade.",
    { period_start: "1930-01-01", period_end: "1939-12-31", period_precision: "decade" }),
  annot("an-05", "narration", "asset", "a-28",
    "She talks about the church, the rain, and her mother's coat. Forty minutes; the good part starts around six minutes in.",
    { attributed_to: "p-margaret", duration_ms: 1_842_000, transcript_state: "edited" }),
  annot("an-06", "correction", "asset", "a-08",
    "This is not 1955. James is clearly younger than in the communion photograph, so it is the early fifties at the latest.",
    { attributed_to: "p-james" }),
  annot("an-07", "narration", "identity", "p-margaret",
    "Margaret Doyle, born in Rathmines, one of six. She left school at fourteen and worked in the laundry on Grand Canal Street.",
    { attributed_to: "p-margaret" }),
  annot("an-08", "journal", "period", null,
    "The years in Roundstone. Every August, the same house.",
    { period_start: "2010-01-01", period_end: "2018-12-31", period_precision: "year" }),
];

// ---------------------------------------------------------------------------
// Albums
// ---------------------------------------------------------------------------

const ALBUM_ITEMS: Record<string, string[]> = {
  "al-weddings": ["a-01", "a-06", "a-17", "a-18"],
  "al-nana": ["a-04", "a-06", "a-07", "a-10", "a-22", "a-26", "a-28", "a-29"],
  "al-attic": ["a-02", "a-03", "a-09", "a-15", "a-36", "a-34"],
  "al-film": ["a-14", "a-20", "a-28"],
};

const ALBUMS: AlbumSummary[] = [
  { id: "al-weddings", name: "Weddings", description: "Four of them, sixty years apart.", visibility: "private", sort: "date", createdBy: "acct-tyler", count: 4, coverHash: hashFor("a-06") },
  { id: "al-nana", name: "Nana", description: "Everything with Margaret in it.", visibility: "private", sort: "date", createdBy: "acct-tyler", count: 8, coverHash: hashFor("a-26") },
  { id: "al-attic", name: "The attic box", description: "Unidentified. Ask at Christmas.", visibility: "private", sort: "added", createdBy: "acct-tyler", count: 6, coverHash: hashFor("a-03") },
  { id: "al-film", name: "Film transfers", description: null, visibility: "private", sort: "date", createdBy: "acct-tyler", count: 3, coverHash: hashFor("a-14") },
];

// ---------------------------------------------------------------------------
// Filtering — the array twin of compile() in cloud/src/views.ts
// ---------------------------------------------------------------------------

function matches(a: AssetCard, f: Filter): boolean {
  if (!f.includeHidden && a.hidden) return false;
  if (f.kind && a.kind !== f.kind) return false;
  if (f.from && (a.capturedAt ?? "") < f.from) return false;
  if (f.to && (a.capturedAt ?? "￿") > f.to) return false;
  if (f.albumId && !(ALBUM_ITEMS[f.albumId] ?? []).includes(a.id)) return false;
  // Every named person must be on the asset, not any of them.
  if (f.people?.length) {
    const on = new Set(a.people.map((p) => p.identityId));
    if (!f.people.every((p) => on.has(p))) return false;
  }
  if (f.unnamed && a.people.some((p) => p.name)) return false;
  if (f.withStories && a.storyCount === 0) return false;
  if (f.q) {
    const hay = [
      a.fileName, a.caption, a.description,
      ...a.people.map((p) => p.name ?? ""),
    ].join(" ").toLowerCase();
    const terms = f.q.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length > 1);
    if (terms.length && !terms.some((t) => hay.includes(t))) return false;
  }
  return true;
}

/** Same ordering rule as the SQL: undated last, then newest first. */
function byDateDesc(a: AssetCard, b: AssetCard): number {
  const av = a.capturedAt ?? "";
  const bv = b.capturedAt ?? "";
  if (!av !== !bv) return av ? -1 : 1;
  if (av !== bv) return av < bv ? 1 : -1;
  return a.id < b.id ? 1 : -1;
}

// ---------------------------------------------------------------------------
// The demo backend
// ---------------------------------------------------------------------------

const wait = <T>(v: T): Promise<T> =>
  new Promise((r) => setTimeout(() => r(v), 90));

/**
 * `?demo=1&empty=1` — a library nobody has put anything in yet.
 *
 * This is the first minute of every archive, and the state most likely to be
 * shipped broken, because it is the one state the person writing the view
 * never sees. It is worth being able to look at on purpose.
 */
const EMPTY = new URLSearchParams(location.search).get("empty") === "1";

/**
 * Sign-in, for `?demo=1&screen=login`. The Worker returns the code in the
 * response body on localhost so the flow is testable without a mail account;
 * this does the same, with a fixed one.
 */
export const DEMO_CODE = "H3IRLM";

export function requestCode(email: string): Promise<{ sent: boolean; code?: string }> {
  if (!/.+@.+\..+/.test(email)) {
    return Promise.reject(new Error("that does not look like an email address"));
  }
  return wait({ sent: true, code: DEMO_CODE });
}

export function verify(_email: string, code: string): Promise<{ accountId: string; created: boolean }> {
  return code.trim().toUpperCase() === DEMO_CODE
    ? wait({ accountId: "acct-tyler", created: false })
    : Promise.reject(new Error("that code was not accepted"));
}

export function me(): Promise<Me> {
  return wait({
    account: { id: "acct-tyler", email: "coffey.d.tyler@gmail.com" },
    libraries: [
      { id: "lib-demo", kind: "personal", name: "Tyler's archive", groupId: null, groupName: null, role: "owner", assetCount: ASSETS.length, bytesStored: 41_231_884_288, storageBytes: 107_374_182_400 },
      { id: "lib-family", kind: "shared", name: "Coffey family", groupId: "g-coffey", groupName: "Coffey", role: "contributor", assetCount: 1284, bytesStored: 88_305_598_464, storageBytes: 214_748_364_800 },
    ],
  });
}

/**
 * Owner capabilities, so the demo shows every control. Append `&role=contributor`
 * to see what a contributor sees — which is the same archive with the
 * destructive controls absent rather than disabled.
 */
export function stats(): Promise<Stats> {
  const contributor = new URLSearchParams(location.search).get("role") === "contributor";
  const CONTRIB: Stats["can"] = [
    "library.read", "asset.add", "album.items.add", "annotation.write",
    "crop.contribute", "identity.create", "identity.name",
    "identity.face.confirm", "relationship.write", "asset.metadata.edit",
  ];
  return wait({
    assets: EMPTY ? 0 : ASSETS.length,
    videos: EMPTY ? 0 : ASSETS.filter((a) => a.kind === "video").length,
    people: EMPTY ? 0 : IDENTITIES.length,
    stories: EMPTY ? 0 : ANNOTATIONS.length,
    awaitingReview: EMPTY ? 0 : 7,
    library: { id: "lib-demo", kind: "personal", name: "Tyler's archive" },
    role: contributor ? "contributor" : "owner",
    can: contributor
      ? CONTRIB
      : [...CONTRIB, "album.create", "album.manage", "asset.edit", "asset.hide", "asset.delete",
         "annotation.delete.any", "duplicate.resolve", "share.create",
        "member.invite", "member.remove", "library.rename", "vision.cloud.run", "reel.render"],
    headroom: 107_374_182_400 - 41_231_884_288,
  });
}

export function cards(
  f: Filter,
  page: { limit?: number; cursor?: string; order?: "date" | "added" | "relevance" },
): Promise<Slice<AssetCard>> {
  if (EMPTY) return wait({ items: [], nextCursor: null, total: 0 });
  const limit = page.limit ?? 60;
  const all = ASSETS.filter((a) => matches(a, f)).sort(byDateDesc);
  const start = page.cursor ? Number(atob(page.cursor)) : 0;
  const items = all.slice(start, start + limit);
  const next = start + limit < all.length ? btoa(String(start + limit)) : null;
  return wait({ items, nextCursor: next, total: all.length });
}

/** The key expressions from viewByDate, transcribed. */
function bucketKey(a: AssetCard, grain: "month" | "year" | "decade"): string {
  if (!a.capturedAt) return "unknown";
  const decade = `${a.capturedAt.slice(0, 3)}0`;
  if (grain === "decade") return decade;
  const fuzzy = a.capturedPrecision === "decade" || a.capturedPrecision === "circa";
  if (fuzzy) return decade;
  if (grain === "year") return a.capturedAt.slice(0, 4);
  return a.capturedPrecision === "year" ? a.capturedAt.slice(0, 4) : a.capturedAt.slice(0, 7);
}

const COARSENESS: Precision[] = ["decade", "circa", "year", "month", "day", "unknown"];

export function dates(f: Filter, grain: "month" | "year" | "decade"): Promise<DateBucket[]> {
  if (EMPTY) return wait([] as DateBucket[]);
  const groups = new Map<string, AssetCard[]>();
  for (const a of ASSETS.filter((x) => matches(x, f))) {
    const k = bucketKey(a, grain);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(a);
  }
  const out: DateBucket[] = [...groups.entries()].map(([key, group]) => {
    // The coarsest precision in the bucket, because that is the most anybody
    // can honestly claim about the group as a whole. NOTE: the live server
    // returns `MIN(captured_precision)`, which is a *lexicographic* minimum and
    // therefore arbitrary — see README, "What is wrong with the API".
    const precision =
      COARSENESS.find((p) => group.some((a) => (a.capturedPrecision ?? "unknown") === p)) ??
      "unknown";
    return {
      key,
      label: key === "unknown" ? "Date unknown" : key,
      precision,
      count: group.length,
      coverHash: group.slice().sort(byDateDesc)[0]?.contentHash ?? null,
    };
  });
  return wait(out.sort((a, b) => (a.key === "unknown" ? 1 : b.key === "unknown" ? -1 : b.key.localeCompare(a.key))));
}

export async function timeline(f: Filter, grain: "year" | "decade"): Promise<TimelineEntry[]> {
  const buckets = await dates(f, grain);
  const out: TimelineEntry[] = [];
  for (const b of buckets) {
    if (b.key === "unknown") continue;
    const from = `${b.key}-01-01`;
    const to = grain === "decade" ? `${Number(b.key) + 9}-12-31` : `${b.key}-12-31`;
    const group = ASSETS.filter((a) => matches(a, { ...f, from, to }));
    const tally = new Map<string, number>();
    for (const a of group) {
      for (const p of a.people) tally.set(p.identityId, (tally.get(p.identityId) ?? 0) + 1);
    }
    out.push({
      bucket: b.key,
      label: b.key,
      from,
      to,
      assetCount: group.length,
      videoCount: group.filter((a) => a.kind === "video").length,
      coverHash: group.slice().sort(byDateDesc)[0]?.contentHash ?? b.coverHash,
      people: [...tally.entries()]
        .sort((x, y) => y[1] - x[1])
        .slice(0, 8)
        .map(([id, n]) => ({ identityId: id, name: NAME.get(id) ?? null, count: n })),
      stories: ANNOTATIONS.filter(
        (an) =>
          an.subject_kind === "period" &&
          (an.period_start ?? "") <= to &&
          (an.period_end ?? an.period_start ?? "") >= from,
      ).map((an) => ({
        id: an.id,
        kind: an.kind,
        body: (an.body ?? "").slice(0, 240),
        attributedTo: an.attributed_to,
      })),
    });
  }
  return out;
}

export function people(): Promise<PersonSummary[]> {
  if (EMPTY) return wait([] as PersonSummary[]);
  return wait(
    IDENTITIES.map((i) => ({
      identityId: i.id,
      name: i.name,
      count: ASSETS.filter((a) => a.people.some((p) => p.identityId === i.id)).length,
      faceCount: ASSETS.filter((a) => a.people.some((p) => p.identityId === i.id)).length,
      coverCrop: `crops/${hashFor(`${i.id}-0`)}`,
      crops: Array.from({ length: Math.min(6, Math.max(1, i.id === "p-unknown" ? 4 : 3)) }, (_, n) =>
        `crops/${hashFor(`${i.id}-${n}`)}`,
      ),
    })).sort((a, b) => b.count - a.count),
  );
}

export function person(id: string): Promise<PersonPage> {
  const who = IDENTITIES.find((i) => i.id === id) ?? IDENTITIES[3]!;
  const mine = ASSETS.filter((a) => a.people.some((p) => p.identityId === who.id));
  const byDecade = new Map<string, number>();
  for (const a of mine) {
    if (!a.capturedAt) continue;
    const d = `${a.capturedAt.slice(0, 3)}0s`;
    byDecade.set(d, (byDecade.get(d) ?? 0) + 1);
  }
  const videos = mine.filter((a) => a.kind === "video");
  return wait({
    identityId: who.id,
    name: who.name,
    birthYear: who.birthYear,
    deathYear: who.deathYear,
    photoCount: mine.filter((a) => a.kind === "image").length,
    videoCount: videos.length,
    firstSeen: mine.map((a) => a.capturedAt).filter(Boolean).sort()[0] ?? null,
    lastSeen: mine.map((a) => a.capturedAt).filter(Boolean).sort().reverse()[0] ?? null,
    overTime: [...byDecade.entries()].sort().map(([decade, count]) => ({ decade, count })),
    appearances: videos.flatMap((v) => {
      const len = v.durationMs ?? 60_000;
      // Three runs per film, the way collapseAppearances would emit them.
      return [0.08, 0.41, 0.73].map((at, i) => {
        const startMs = Math.round(len * at);
        const endMs = Math.min(len, startMs + 11_000 + i * 4000);
        return {
          assetId: v.id,
          fileName: v.fileName,
          contentHash: v.contentHash,
          durationMs: v.durationMs,
          startMs,
          endMs,
          bestFrameMs: Math.round((startMs + endMs) / 2),
        };
      });
    }),
    crops: [
      { id: `c-${who.id}-1`, cropKey: `crops/${who.id}-1.jpg`, quality: 0.94, chosen: false,
      sourceAsset: null },
      { id: `c-${who.id}-2`, cropKey: `crops/${who.id}-2.jpg`, quality: 0.81, chosen: false,
      sourceAsset: null },
    ],
    relatives: relativesOf(who.id),
    stories: ANNOTATIONS.filter((an) => an.attributed_to === who.id),
  });
}

export function albums(): Promise<AlbumSummary[]> {
  if (EMPTY) return wait([] as AlbumSummary[]);
  return wait(ALBUMS);
}

let DEMO_SHARES: ManagedShare[] = [
  {
    id: "share-demo-1",
    email: "susan@example.com",
    scope: "album",
    albumId: "al-weddings",
    albumName: "Weddings",
    permission: "view",
    status: "active",
    subscriptionStatus: "not_checked",
    deliveryStatus: "preview",
    createdAt: "2026-09-12 12:00:00",
    expiresAt: "2026-10-12 12:00:00",
  },
];

export function shares(): Promise<ManagedShare[]> {
  return wait(DEMO_SHARES.slice());
}

export function inviteShare(body: {
  email: string;
  scope: "album" | "archive";
  permission: "view" | "edit";
  albumId?: string;
}): Promise<ManagedShare> {
  const album = body.albumId ? ALBUMS.find((candidate) => candidate.id === body.albumId) : null;
  const active = body.scope === "album" && body.permission === "view";
  const share: ManagedShare = {
    id: crypto.randomUUID(),
    email: body.email.trim().toLowerCase(),
    scope: body.scope,
    albumId: body.scope === "album" ? body.albumId ?? null : null,
    albumName: album?.name ?? null,
    permission: body.permission,
    status: active ? "active" : "pending_subscription",
    subscriptionStatus: "not_checked",
    deliveryStatus: "preview",
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 30 * 86400_000).toISOString(),
  };
  DEMO_SHARES = [share, ...DEMO_SHARES];
  return wait(share);
}

export function revokeShare(shareId: string): Promise<unknown> {
  DEMO_SHARES = DEMO_SHARES.filter((share) => share.id !== shareId);
  return wait({ revoked: shareId });
}

// ---------------------------------------------------------------------------
// The demo backend's writes
// ---------------------------------------------------------------------------
// The three capability-gated controls in the client have to be pressable in
// demo mode, or the gating cannot be seen working. These mutate the arrays
// above, exactly as far as one page load lasts.

export function setHidden(assetId: string, hidden: boolean): Promise<unknown> {
  const a = ASSETS.find((x) => x.id === assetId);
  if (a) a.hidden = hidden;
  return wait({ ok: true });
}

export function patchMetadata(
  assetId: string,
  patch: { caption?: string | null; description?: string | null },
): Promise<unknown> {
  const asset = ASSETS.find((item) => item.id === assetId);
  if (asset) Object.assign(asset, patch);
  return wait({ ok: true });
}

export function setDate(
  assetId: string,
  capturedAt: string | null,
  precision: Precision | null,
): Promise<unknown> {
  const asset = ASSETS.find((item) => item.id === assetId);
  if (asset) {
    asset.capturedAt = capturedAt;
    asset.capturedPrecision = precision;
    asset.dateSource = "user";
  }
  return wait({ ok: true });
}

export function nameIdentity(identityId: string, name: string): Promise<unknown> {
  const who = IDENTITIES.find((i) => i.id === identityId);
  if (who) who.name = name;
  NAME.set(identityId, name);
  // A name belongs to the person, so it appears on every photograph they are
  // in — which is the whole reason naming is worth doing once.
  for (const a of ASSETS) {
    for (const p of a.people) if (p.identityId === identityId) p.name = name;
  }
  return wait({ named: identityId });
}

export function createAlbum(name: string, description?: string): Promise<unknown> {
  const id = `al-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  ALBUM_ITEMS[id] = [];
  ALBUMS.push({
    id, name,
    description: description ?? null,
    visibility: "private",
    sort: "date",
    createdBy: "acct-tyler",
    count: 0,
    coverHash: null,
  });
  return wait({ albumId: id });
}

export function journal(): Promise<Annotation[]> {
  if (EMPTY) return wait([] as Annotation[]);
  return wait(
    ANNOTATIONS.filter((a) => ["journal", "narration", "note"].includes(a.kind)),
  );
}

// ---------------------------------------------------------------------------
// Tree — generations and kinship, ported from cloud/src/tree.ts
// ---------------------------------------------------------------------------

const parents = new Map<string, Set<string>>();
const children = new Map<string, Set<string>>();
const spouses = new Map<string, Set<string>>();
for (const e of RELATIONSHIPS) {
  const add = (m: Map<string, Set<string>>, k: string, v: string) =>
    m.set(k, (m.get(k) ?? new Set()).add(v));
  if (e.kind === "parent") {
    add(parents, e.to, e.from);
    add(children, e.from, e.to);
  } else {
    add(spouses, e.from, e.to);
    add(spouses, e.to, e.from);
  }
}

function ancestorDistances(who: string): Map<string, number> {
  const dist = new Map([[who, 0]]);
  let frontier = [who];
  for (let d = 1; d <= 12 && frontier.length; d++) {
    const next: string[] = [];
    for (const p of frontier) {
      for (const par of parents.get(p) ?? []) {
        if (!dist.has(par)) {
          dist.set(par, d);
          next.push(par);
        }
      }
    }
    frontier = next;
  }
  return dist;
}

const UP = ["", "parent", "grandparent", "great-grandparent"];
const DOWN = ["", "child", "grandchild", "great-grandchild"];
const NIBLING = ["", "sibling", "aunt or uncle", "great-aunt or great-uncle"];

function kinship(root: string, other: string, depth = 0): string | null {
  if (root === other) return null;
  if (spouses.get(root)?.has(other)) return "spouse";
  const a = ancestorDistances(root);
  const b = ancestorDistances(other);
  let best: { m: number; n: number } | null = null;
  for (const [anc, m] of a) {
    const n = b.get(anc);
    if (n === undefined) continue;
    if (!best || m + n < best.m + best.n) best = { m, n };
  }
  if (!best) {
    if (depth > 1) return null;
    for (const sp of spouses.get(other) ?? []) {
      const via = kinship(root, sp, depth + 1);
      if (via && via !== "spouse") return `${via}'s spouse`;
    }
    return null;
  }
  const { m, n } = best;
  if (m === 0) return n <= 3 ? DOWN[n]! : `${"great-".repeat(n - 2)}grandchild`;
  if (n === 0) return m <= 3 ? UP[m]! : `${"great-".repeat(m - 2)}grandparent`;
  if (m === 1 && n === 1) return "sibling";
  if (n === 1) return m <= 3 ? NIBLING[m]! : `${"great-".repeat(m - 2)}aunt or uncle`;
  if (m === 1) return n === 2 ? "niece or nephew" : `${"great-".repeat(n - 2)}niece or nephew`;
  const degree = Math.min(m, n) - 1;
  const removed = Math.abs(m - n);
  const ord = ["", "first", "second", "third", "fourth", "fifth"][degree] ?? `${degree}th`;
  if (removed === 0) return `${ord} cousin`;
  const times = removed === 1 ? "once" : removed === 2 ? "twice" : `${removed} times`;
  return `${ord} cousin ${times} removed`;
}

const RANK = ["parent", "child", "spouse", "sibling", "grandparent", "grandchild",
               "aunt or uncle", "niece or nephew"];

function relativesOf(id: string) {
  const out: Array<{ identityId: string; name: string | null; kinship: string }> = [];
  for (const i of IDENTITIES) {
    if (i.id === id) continue;
    const k = kinship(id, i.id);
    if (k) out.push({ identityId: i.id, name: i.name, kinship: k });
  }
  return out.sort((a, b) => {
    const ra = RANK.indexOf(a.kinship);
    const rb = RANK.indexOf(b.kinship);
    return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
  });
}

function generations(root: string): Map<string, number> {
  const gen = new Map([[root, 0]]);
  const queue = [root];
  while (queue.length) {
    const who = queue.shift()!;
    const here = gen.get(who)!;
    const step = (other: string, level: number) => {
      if (!gen.has(other)) {
        gen.set(other, level);
        queue.push(other);
      }
    };
    for (const p of parents.get(who) ?? []) step(p, here - 1);
    for (const c of children.get(who) ?? []) step(c, here + 1);
    for (const s of spouses.get(who) ?? []) step(s, here);
  }
  return gen;
}

const photoCount = (id: string) =>
  ASSETS.filter((a) => a.people.some((p) => p.identityId === id)).length;

export function tree(opts: {
  root?: string;
  depth?: number;
  direction?: "both" | "up" | "down";
  photographed?: boolean;
}): Promise<Tree> {
  if (EMPTY) return wait({ rootId: null, nodes: [], edges: [], roots: [], unplaced: 0 });
  const rootId =
    opts.root ??
    IDENTITIES.reduce((a, b) => (photoCount(b.id) > photoCount(a.id) ? b : a)).id;
  const gen = generations(rootId);
  const depth = opts.depth ?? 4;
  const dir = opts.direction ?? "both";

  const nodes: TreeNode[] = [];
  for (const i of IDENTITIES) {
    const level = gen.get(i.id);
    if (level === undefined) continue;
    if (Math.abs(level) > depth) continue;
    if (dir === "up" && level > 0) continue;
    if (dir === "down" && level < 0) continue;
    const n = photoCount(i.id);
    if (opts.photographed && n === 0 && i.id !== rootId) continue;
    nodes.push({
      identityId: i.id,
      name: i.name,
      birthYear: i.birthYear,
      deathYear: i.deathYear,
      photoCount: n,
      coverCrop: `crops/${i.id}.jpg`,
      generation: level,
      kinship: kinship(rootId, i.id),
    });
  }
  const kept = new Set(nodes.map((n) => n.identityId));
  return wait({
    rootId,
    nodes: nodes.sort(
      (a, b) => a.generation - b.generation || (b.birthYear ?? 0) - (a.birthYear ?? 0),
    ),
    edges: RELATIONSHIPS.filter((e) => kept.has(e.from) && kept.has(e.to)) as Array<
      TreeEdge & { certainty: Certainty }
    >,
    roots: [...kept].filter((id) => !parents.get(id)?.size),
    unplaced: IDENTITIES.length - gen.size,
  });
}

// ---------------------------------------------------------------------------
// Stand-in imagery
// ---------------------------------------------------------------------------

/**
 * There are no bytes in demo mode, so a blob URL becomes a plate: the hash's
 * own colours, and the hash printed on it. Deliberately not a grey box — a grid
 * of identical grey boxes tells you nothing about whether the grid works.
 */
export function placeholderImage(hash: string): string {
  const n = parseInt(hash.slice(0, 6), 16);
  const hue = n % 360;
  const hue2 = (hue + 40) % 360;
  const s = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240">
<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
<stop offset="0" stop-color="hsl(${hue} 24% 26%)"/>
<stop offset="1" stop-color="hsl(${hue2} 30% 14%)"/></linearGradient></defs>
<rect width="320" height="240" fill="url(#g)"/>
<circle cx="${100 + (n % 120)}" cy="${70 + (n % 60)}" r="${28 + (n % 22)}" fill="hsl(${hue2} 40% 60%)" opacity=".28"/>
<text x="12" y="228" fill="hsl(${hue} 20% 78%)" opacity=".7"
 font-family="ui-monospace,monospace" font-size="11">${hash.slice(0, 12)}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`;
}
